/**
 * OrdersCard (Slice O5) — the commercial Order layer inside the per-customer Plan & Entitlements
 * slideout. Build an Order, add line items (edition/modules · size · mode · support · SLA · billing),
 * then Apply a line to provision an Instance + seed the customer's entitlements. Apply resolves the
 * tenant explicitly (Instance → Tenant → Org → Customer) via an inline Org ▸ Tenant picker.
 *
 * Backend: PA-only /api/v1/admin/orders (OrderController + OrderProvisioningService). This card is
 * purely additive — it sits atop the existing sections and drives the Edition/Modules below it.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Play, Trash2, ChevronDown, ChevronRight, Check, X, Pencil, Receipt } from 'lucide-react';
import { Card } from './Card';
import { Button } from './Button';
import { Pill } from './Pill';
import * as api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

// Canonical vocabularies (dropdown values; the backend validates authoritatively).
const SIZES = ['XS', 'S', 'M', 'LG', 'XL'];
const MODES = [['prove7_hosted', 'Prove7 Cloud'], ['self_hosted', 'Self-Hosted']];
const SLAS = ['single_az', 'multi_az', 'multi_region_active_standby', 'multi_region_active_active'];
const RESIDENCY = ['region_locked', 'backup_export_same_region', 'cross_region_to_paired'];
const SUPPORT = ['STANDARD', 'PREMIUM', 'PREMIER'];
const BILLING = ['PRE_COMMITTED', 'USAGE_BASED'];
const EDITIONS = ['GOVERNED_WORKFLOW', 'AGENTIC_PACK', 'AGENTIC_GOVERNANCE', 'CONTINUOUS_TRUST'];
// Friendly labels for edition keys (mirrors EditionCatalog display names).
const EDITION_LABELS = {
  GOVERNED_WORKFLOW: 'Governed Workflow',
  AGENTIC_PACK: 'Agentic Pack',
  AGENTIC_GOVERNANCE: 'Agentic Governance',
  CONTINUOUS_TRUST: 'Continuous Trust',
};
const editionLabel = (k) => (k ? (EDITION_LABELS[k] || k) : null);
const MODULES = ['CORE', 'WORKFLOW_ORCH', 'WORKFLOW_PROTECT', 'GOVERNANCE', 'AGENT_GOV',
  'EXT_AGENT_GOV', 'SHADOW_DISCOVERY', 'TRUST_COMPLIANCE', 'IDENTITY_AUTHZ', 'API_GATEWAY', 'PROVE7_CONTAINER'];
const TERMS = ['MONTHLY', 'QUARTERLY', 'YEARLY', 'CUSTOM'];
const STATUS_TONE = { DRAFT: 'muted', PROVISIONED: 'success', ACTIVE: 'action', CANCELLED: 'danger', SUPERSEDED: 'muted' };

const errMsg = (e) => e?.response?.data?.message || e?.message || 'Request failed';

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] text-v2-ink-secondary font-semibold mb-1">{label}</span>
      {children}
    </label>
  );
}
const selCls = 'w-full border border-v2-border-default rounded-md px-2 py-1.5 text-[12px] bg-v2-bg-elevated text-v2-ink';

export function OrdersCard({ customer, onChanged }) {
  const cid = customer?.id;
  const { addToast } = useToast();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);          // { order, lines }
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);  // id pending delete confirm

  const [newOrder, setNewOrder] = useState(null);      // create-order form or null
  const [lineForm, setLineForm] = useState(null);      // { orderId, lineId?, ...fields }
  const [apply, setApply] = useState(null);            // { orderId, lineId }
  const [orgs, setOrgs] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [applySel, setApplySel] = useState({ orgId: '', tenantId: '' });

  const notifyErr = useCallback((e) => addToast?.({ tone: 'danger', message: errMsg(e) }), [addToast]);
  const notifyOk = useCallback((m) => addToast?.({ tone: 'success', message: m }), [addToast]);

  const loadOrders = useCallback(async () => {
    if (!cid) return;
    setLoading(true);
    try {
      const res = await api.listOrders(cid);
      setOrders(Array.isArray(res) ? res : (res?.data ?? []));
    } catch (e) { notifyErr(e); } finally { setLoading(false); }
  }, [cid, notifyErr]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const openOrder = async (id) => {
    if (openId === id) { setOpenId(null); setDetail(null); return; }
    setOpenId(id); setDetail(null); setApply(null); setLineForm(null);
    try {
      const res = await api.getOrder(id);
      setDetail(res?.data ?? res);
    } catch (e) { notifyErr(e); }
  };
  const refreshDetail = async (id) => {
    try { const res = await api.getOrder(id); setDetail(res?.data ?? res); } catch (e) { notifyErr(e); }
  };

  // ── orders ──
  const submitNewOrder = async () => {
    setBusy(true);
    try {
      await api.createOrder({ ...newOrder, customerId: cid });
      setNewOrder(null);
      await loadOrders();
      notifyOk('Order created');
    } catch (e) { notifyErr(e); } finally { setBusy(false); }
  };
  const doDeleteOrder = async (id) => {
    setBusy(true);
    try { await api.deleteOrder(id); setConfirmDel(null); if (openId === id) { setOpenId(null); setDetail(null); } await loadOrders(); notifyOk('Order deleted'); }
    catch (e) { notifyErr(e); } finally { setBusy(false); }
  };

  // ── lines ──
  const submitLine = async () => {
    const { orderId, lineId, ...fields } = lineForm;
    const body = {
      ...fields,
      quantity: fields.quantity ? Number(fields.quantity) : 1,
      discountPct: fields.discountPct === '' || fields.discountPct == null ? null : Number(fields.discountPct),
      modules: fields.editionKey ? null : (fields.modules || []),
      editionKey: fields.editionKey || null,
    };
    setBusy(true);
    try {
      if (lineId) await api.updateOrderLine(lineId, body);
      else await api.addOrderLine(orderId, body);
      setLineForm(null);
      await refreshDetail(orderId);
      notifyOk(lineId ? 'Line updated' : 'Line added');
    } catch (e) { notifyErr(e); } finally { setBusy(false); }
  };
  const doDeleteLine = async (orderId, lineId) => {
    setBusy(true);
    try { await api.deleteOrderLine(lineId); setConfirmDel(null); await refreshDetail(orderId); notifyOk('Line deleted'); }
    catch (e) { notifyErr(e); } finally { setBusy(false); }
  };

  // ── apply ──
  const beginApply = async (orderId, lineId, line) => {
    setApply({ orderId, lineId });
    setApplySel({ orgId: line?.targetOrgId || '', tenantId: line?.targetTenantId || '' });
    setTenants([]);
    try {
      const res = await api.getOrganizations();
      const all = Array.isArray(res) ? res : (res?.data ?? []);
      setOrgs(all.filter((o) => o.customerId === cid));
    } catch (e) { notifyErr(e); }
  };
  const onPickOrg = async (orgId) => {
    setApplySel({ orgId, tenantId: '' });
    setTenants([]);
    if (!orgId) return;
    try {
      const res = await api.getTenantsByOrg(orgId);
      setTenants(Array.isArray(res) ? res : (res?.data ?? []));
    } catch (e) { notifyErr(e); }
  };
  const confirmApply = async () => {
    if (!applySel.tenantId) { addToast?.({ tone: 'danger', message: 'Select a target tenant' }); return; }
    setBusy(true);
    try {
      await api.applyOrderLine(apply.orderId, apply.lineId, { orgId: applySel.orgId || null, tenantId: applySel.tenantId });
      setApply(null);
      await refreshDetail(apply.orderId);
      onChanged?.();
      notifyOk('Line provisioned');
    } catch (e) { notifyErr(e); } finally { setBusy(false); }
  };

  const blankLine = (orderId) => ({
    orderId, editionKey: 'AGENTIC_GOVERNANCE', modules: [], quantity: 1,
    instanceSize: 'M', deploymentMode: 'prove7_hosted', region: '', supportType: 'STANDARD',
    slaTier: 'single_az', dataResidency: 'region_locked', billingMode: 'PRE_COMMITTED',
    term: 'YEARLY', discountPct: '',
  });

  if (!cid) return null;

  return (
    <Card padding="sm">
      <Card.Body>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Pill tone="action" size="sm">Orders &amp; provisioning</Pill>
            <span className="text-[11px] text-v2-ink-muted">{orders.length} order{orders.length === 1 ? '' : 's'}</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Preview invoice — reuses the SHARED billing endpoint (same as the panel's Billing card).
              * The assembler reads the customer's live orders + entitlements, so this always reflects the
              * latest applied line/module. No duplicate path. */}
            <Button variant="ghost" size="sm" iconLeft={<Receipt size={12} />} disabled={!cid}
              onClick={() => { if (cid) window.open(api.invoicePreviewHtmlUrl(cid), '_blank', 'noopener'); }}>Preview invoice</Button>
            <Button variant="secondary" size="sm" iconLeft={<Plus size={12} />}
              onClick={() => setNewOrder({ name: '', term: 'YEARLY', currency: 'USD', discountPct: '' })}>New order</Button>
          </div>
        </div>

        {loading && <div className="text-[12px] text-v2-ink-muted">Loading orders…</div>}

        {newOrder && (
          <div className="border border-v2-border-subtle rounded-md p-3 mb-3 bg-v2-bg-elevated">
            <div className="grid grid-cols-2 gap-2 mb-2">
              <Field label="Order name / number"><input className={selCls} value={newOrder.name}
                onChange={(e) => setNewOrder({ ...newOrder, name: e.target.value })} placeholder="ACME-2026-Q3" /></Field>
              <Field label="Term"><select className={selCls} value={newOrder.term}
                onChange={(e) => setNewOrder({ ...newOrder, term: e.target.value })}>{TERMS.map((t) => <option key={t}>{t}</option>)}</select></Field>
              <Field label="Currency"><input className={selCls} value={newOrder.currency}
                onChange={(e) => setNewOrder({ ...newOrder, currency: e.target.value.toUpperCase() })} maxLength={3} /></Field>
              <Field label="Discount %"><input className={selCls} type="number" value={newOrder.discountPct}
                onChange={(e) => setNewOrder({ ...newOrder, discountPct: e.target.value })} placeholder="0" /></Field>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setNewOrder(null)} disabled={busy}>Cancel</Button>
              <Button variant="primary" size="sm" onClick={submitNewOrder}
                disabled={busy} iconLeft={<Check size={12} />}>Create</Button>
            </div>
          </div>
        )}

        {!loading && orders.length === 0 && !newOrder && (
          <div className="text-[12px] text-v2-ink-muted py-2">No orders yet. Create one to provision instances &amp; entitlements.</div>
        )}

        <div className="space-y-1.5">
          {orders.map((o) => {
            const isOpen = openId === o.id;
            return (
              <div key={o.id} className="border border-v2-border-subtle rounded-md overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-v2-bg-elevated" onClick={() => openOrder(o.id)}>
                  {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                  <span className="font-semibold text-[12.5px] text-v2-ink">{o.name || o.orderNumber || o.id.slice(0, 8)}</span>
                  <Pill tone={STATUS_TONE[o.status] || 'muted'} size="sm">{o.status}</Pill>
                  <span className="text-[11px] text-v2-ink-muted">{o.term} · {o.currency}</span>
                  <span className="ml-auto" onClick={(e) => e.stopPropagation()}>
                    {o.status === 'DRAFT' && (confirmDel === `o:${o.id}`
                      ? <span className="inline-flex gap-1">
                          <Button variant="danger" size="sm" onClick={() => doDeleteOrder(o.id)} disabled={busy}>Confirm</Button>
                          <Button variant="ghost" size="sm" onClick={() => setConfirmDel(null)}>Cancel</Button>
                        </span>
                      : <Button variant="ghost" size="sm" iconLeft={<Trash2 size={12} />} onClick={() => setConfirmDel(`o:${o.id}`)}>Delete</Button>)}
                  </span>
                </div>

                {isOpen && (
                  <div className="border-t border-v2-border-subtle p-3 bg-v2-bg-base">
                    {!detail && <div className="text-[12px] text-v2-ink-muted">Loading…</div>}
                    {detail && (
                      <>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[11px] text-v2-ink-secondary">{(detail.lines || []).length} line item(s)</span>
                          <span className="flex gap-2">
                            <Button variant="secondary" size="sm" iconLeft={<Plus size={12} />}
                              onClick={() => setLineForm(blankLine(o.id))}>Add line</Button>
                            <Button variant="primary" size="sm" iconLeft={<Play size={12} />}
                              onClick={async () => { setBusy(true); try { await api.applyOrder(o.id); await refreshDetail(o.id); onChanged?.(); notifyOk('Applied all eligible lines'); } catch (e) { notifyErr(e); } finally { setBusy(false); } }}>Apply all</Button>
                          </span>
                        </div>

                        <div className="space-y-1">
                          {(detail.lines || []).map((l) => (
                            <div key={l.id} className="text-[12px] border border-v2-border-subtle rounded-md p-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-v2-ink">#{l.lineNo ?? '—'}</span>
                                <span className="text-v2-ink">
                                  {editionLabel(l.editionKey) || (Array.isArray(l.modules) && l.modules.length ? l.modules.join(', ') : 'modules')}
                                  {l.quantity > 1 ? <span className="text-v2-ink-muted"> · ×{l.quantity}</span> : null}
                                </span>
                                <span className="text-v2-ink-muted">{l.instanceSize} · {l.deploymentMode}</span>
                                <span className="text-v2-ink-muted">{l.supportType} · {l.slaTier}</span>
                                <Pill tone={STATUS_TONE[l.status] || 'muted'} size="sm">{l.status}</Pill>
                                <span className="ml-auto flex gap-1">
                                  {l.status === 'DRAFT' && <>
                                    <Button variant="primary" size="sm" iconLeft={<Play size={11} />} onClick={() => beginApply(o.id, l.id, l)}>Apply</Button>
                                    <Button variant="ghost" size="sm" iconLeft={<Pencil size={11} />} onClick={() => setLineForm({ ...blankLine(o.id), ...l, lineId: l.id, orderId: o.id, modules: Array.isArray(l.modules) ? l.modules : [] })}>Edit</Button>
                                    {confirmDel === `l:${l.id}`
                                      ? <><Button variant="danger" size="sm" onClick={() => doDeleteLine(o.id, l.id)} disabled={busy}>Confirm</Button>
                                          <Button variant="ghost" size="sm" onClick={() => setConfirmDel(null)}>Cancel</Button></>
                                      : <Button variant="ghost" size="sm" iconLeft={<Trash2 size={11} />} onClick={() => setConfirmDel(`l:${l.id}`)}>Delete</Button>}
                                  </>}
                                  {l.status === 'PROVISIONED' && <span className="text-[11px] text-v2-success font-mono">✓ {String(l.provisionedInstanceId || '').slice(0, 8)}</span>}
                                </span>
                              </div>

                              {apply && apply.lineId === l.id && (
                                <div className="mt-2 border-t border-v2-border-subtle pt-2 bg-v2-bg-elevated rounded p-2">
                                  <div className="text-[11px] font-semibold text-v2-ink mb-1">Apply — resolve tenant (Instance → Tenant → Org → Customer)</div>
                                  <div className="grid grid-cols-2 gap-2 mb-2">
                                    <Field label="Organization"><select className={selCls} value={applySel.orgId} onChange={(e) => onPickOrg(e.target.value)}>
                                      <option value="">Select org…</option>
                                      {orgs.map((org) => <option key={org.id} value={org.id}>{org.name || org.id.slice(0, 8)}</option>)}
                                    </select></Field>
                                    <Field label="Tenant"><select className={selCls} value={applySel.tenantId} onChange={(e) => setApplySel({ ...applySel, tenantId: e.target.value })} disabled={!applySel.orgId}>
                                      <option value="">Select tenant…</option>
                                      {tenants.map((t) => <option key={t.id} value={t.id}>{t.name || t.id.slice(0, 8)}</option>)}
                                    </select></Field>
                                  </div>
                                  <div className="text-[11px] text-v2-ink-muted mb-2">
                                    Will create an Instance ({l.instanceSize} · {l.deploymentMode}) in the chosen tenant and seed {l.editionKey || 'the selected modules'}{l.supportType ? ` + support ${l.supportType}` : ''}.
                                  </div>
                                  <div className="flex justify-end gap-2">
                                    <Button variant="ghost" size="sm" iconLeft={<X size={12} />} onClick={() => setApply(null)}>Cancel</Button>
                                    <Button variant="primary" size="sm" iconLeft={<Check size={12} />} onClick={confirmApply} disabled={busy || !applySel.tenantId}>Provision line</Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                          {(detail.lines || []).length === 0 && <div className="text-[11px] text-v2-ink-muted py-1">No line items yet.</div>}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {lineForm && (
          <div className="border border-v2-action rounded-md p-3 mt-3 bg-v2-bg-elevated">
            <div className="text-[12px] font-semibold text-v2-ink mb-2">{lineForm.lineId ? 'Edit line' : 'New line item'}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              <Field label="Edition"><select className={selCls} value={lineForm.editionKey || ''} onChange={(e) => setLineForm({ ...lineForm, editionKey: e.target.value })}>
                <option value="">— none (pick modules)</option>{EDITIONS.map((x) => <option key={x} value={x}>{EDITION_LABELS[x] || x}</option>)}</select></Field>
              <Field label="Quantity"><input className={selCls} type="number" min="1" value={lineForm.quantity} onChange={(e) => setLineForm({ ...lineForm, quantity: e.target.value })} /></Field>
              <Field label="Instance size"><select className={selCls} value={lineForm.instanceSize} onChange={(e) => setLineForm({ ...lineForm, instanceSize: e.target.value })}>{SIZES.map((x) => <option key={x}>{x}</option>)}</select></Field>
              <Field label="Deployment mode"><select className={selCls} value={lineForm.deploymentMode} onChange={(e) => setLineForm({ ...lineForm, deploymentMode: e.target.value })}>{MODES.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}</select></Field>
              <Field label="Support"><select className={selCls} value={lineForm.supportType} onChange={(e) => setLineForm({ ...lineForm, supportType: e.target.value })}>{SUPPORT.map((x) => <option key={x}>{x}</option>)}</select></Field>
              <Field label="SLA tier"><select className={selCls} value={lineForm.slaTier} onChange={(e) => setLineForm({ ...lineForm, slaTier: e.target.value })}>{SLAS.map((x) => <option key={x}>{x}</option>)}</select></Field>
              <Field label="Billing mode"><select className={selCls} value={lineForm.billingMode} onChange={(e) => setLineForm({ ...lineForm, billingMode: e.target.value })}>{BILLING.map((x) => <option key={x}>{x}</option>)}</select></Field>
              <Field label="Region"><input className={selCls} value={lineForm.region || ''} onChange={(e) => setLineForm({ ...lineForm, region: e.target.value })} placeholder="us-east-1" /></Field>
              <Field label="Data residency"><select className={selCls} value={lineForm.dataResidency} onChange={(e) => setLineForm({ ...lineForm, dataResidency: e.target.value })}>{RESIDENCY.map((x) => <option key={x}>{x}</option>)}</select></Field>
              <Field label="Discount %"><input className={selCls} type="number" value={lineForm.discountPct} onChange={(e) => setLineForm({ ...lineForm, discountPct: e.target.value })} placeholder="0" /></Field>
            </div>
            {!lineForm.editionKey && (
              <div className="mt-2">
                <div className="text-[11px] text-v2-ink-secondary font-semibold mb-1">Modules</div>
                <div className="flex flex-wrap gap-1.5">
                  {MODULES.map((m) => {
                    const on = (lineForm.modules || []).includes(m);
                    return <button key={m} type="button"
                      onClick={() => setLineForm({ ...lineForm, modules: on ? lineForm.modules.filter((x) => x !== m) : [...(lineForm.modules || []), m] })}
                      className={'text-[11px] px-2 py-0.5 rounded-v2-pill border ' + (on ? 'bg-v2-action-soft border-v2-action text-v2-action-strong' : 'border-v2-border-default text-v2-ink-secondary')}>{m}</button>;
                  })}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="ghost" size="sm" onClick={() => setLineForm(null)} disabled={busy}>Cancel</Button>
              <Button variant="primary" size="sm" iconLeft={<Check size={12} />} onClick={submitLine} disabled={busy}>{lineForm.lineId ? 'Save line' : 'Add line'}</Button>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default OrdersCard;
